import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { FilterMenu } from "@/components/ui/filter-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableStateRow } from "@/components/ui/table-state-row";
import { DraftInProgressPill } from "@/components/settings/nutrition-shared";
import { useDebounce } from "@/hooks/use-debounce";
import { useUrlListState } from "@/hooks/use-url-list-state";
import { useRecipes } from "@/hooks/useRecipes";
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import type { RecipeStatus } from "@/lib/recipe-api";

type StatusTab = "all" | RecipeStatus;

const SORT_OPTIONS = [
  { value: "-updated_at", label: "Most recent" },
  { value: "name", label: "A → Z" },
  { value: "-name", label: "Z → A" },
  { value: "-energy_kcal_per_serving", label: "Calories (high → low)" },
  { value: "energy_kcal_per_serving", label: "Calories (low → high)" },
];

const formatRelative = (iso: string) => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
  }).format(date);
};

export const ViewRecipes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Search, category, tab and sort live in the URL, so coming back from a
  // recipe lands on the same results instead of the full list.
  const [listState, setListState] = useUrlListState({
    q: "",
    category: [] as string[],
    tab: "all",
    sort: "-updated_at",
  });
  const searchInput = listState.q;
  const categoryFilter = listState.category;
  const statusTab = listState.tab as StatusTab;
  const sortBy = listState.sort;
  const setSearchInput = (q: string) => setListState({ q });
  const setStatusTab = (tab: StatusTab) => setListState({ tab });
  const setSortBy = (sort: string) => setListState({ sort });

  const { data: tagMetadata } = useTagMetadata();
  const categoryOptions = useMemo(
    () =>
      (tagMetadata?.classification_tags.meal_types ?? []).map((tag) => ({
        value: tag.name,
        label: tag.name,
      })),
    [tagMetadata]
  );
  const toggleCategory = (value: string) =>
    setListState({
      category: categoryFilter.includes(value)
        ? categoryFilter.filter((v) => v !== value)
        : [...categoryFilter, value],
    });

  const debouncedSearch = useDebounce(searchInput, 300);
  const recipeQueryParams = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: statusTab === "all" ? undefined : statusTab,
      // Several categories go comma-separated; the backend matches any of them.
      category: categoryFilter.length ? categoryFilter.join(",") : undefined,
      ordering: sortBy,
      page_size: 100,
    }),
    [categoryFilter, debouncedSearch, sortBy, statusTab]
  );
  const { data, isLoading, isError, refetch } = useRecipes(recipeQueryParams);
  const rows = useMemo(() => data?.results ?? [], [data?.results]);

  // Tab counts must NOT be read from the status-filtered request: status_counts
  // is scoped to the request's own filters (brief §3), so a status=draft
  // request zeroes out the Submitted count. Drive counts from a sibling query
  // that omits `status` (React Query dedupes it with the main query while the
  // "All" tab is active, so this is a single request in the common case).
  const countsParams = useMemo(
    () => ({ ...recipeQueryParams, status: undefined }),
    [recipeQueryParams]
  );
  const { data: countsData } = useRecipes(countsParams);

  const tabCounts = useMemo(() => {
    const counts = countsData?.status_counts;
    const draft = counts?.draft ?? 0;
    const submitted = counts?.submitted ?? 0;
    return {
      all: countsData?.count ?? draft + submitted,
      draft,
      submitted,
    };
  }, [countsData]);

  const hasActiveFilters =
    Boolean(debouncedSearch.trim()) ||
    categoryFilter.length > 0 ||
    statusTab !== "all";
  const hasCategoryFilter = categoryFilter.length > 0;

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <CardTitle className="z-h2">Recipes</CardTitle>
              <p className="z-caption text-muted-foreground">
                Combine food items into reusable recipes. Each saved recipe
                becomes a food item available to log in any consultation.
              </p>
            </div>
            <Button
              onClick={() => navigate("/settings/nutrition/add-recipe")}
              size="sm"
              className="self-start md:self-auto"
            >
              <Plus className="size-4" />
              Create recipe
            </Button>
          </div>

          <Tabs
            value={statusTab}
            onValueChange={(v) => setStatusTab(v as StatusTab)}
          >
            <TabsList variant="line">
              <TabsTrigger value="all">All ({tabCounts.all})</TabsTrigger>
              <TabsTrigger value="draft">
                Drafts ({tabCounts.draft})
              </TabsTrigger>
              <TabsTrigger value="submitted">
                Published ({tabCounts.submitted})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <SearchInput
            placeholder="Search recipes..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
            containerClassName="w-full md:max-w-md"
          />

          <div className="flex flex-wrap items-center gap-2">
            <FilterMenu
              label="Category"
              options={categoryOptions}
              value={categoryFilter}
              onValueChange={toggleCategory}
              multiple
            />
            <div className="ml-auto flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger size="sm" className="w-auto gap-1.5">
                  <span className="text-muted-foreground">Sort:</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasCategoryFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setListState({ category: [] })}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col overflow-hidden">
          <div className="mb-3 z-caption text-muted-foreground">
            {isLoading
              ? "Loading recipes..."
              : isError
                ? "Could not load recipes."
                : `Showing ${rows.length} of ${data?.count ?? rows.length} recipes`}
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </div>

          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Name
                  </TableHead>
                  <TableHead className="w-[140px] z-eyebrow text-muted-foreground">
                    Category
                  </TableHead>
                  <TableHead className="w-[100px] text-right z-eyebrow text-muted-foreground">
                    Servings
                  </TableHead>
                  <TableHead className="w-[140px] text-right z-eyebrow text-muted-foreground">
                    kcal / serving
                  </TableHead>
                  <TableHead className="w-[120px] text-right z-eyebrow text-muted-foreground">
                    Ingredients
                  </TableHead>
                  <TableHead className="w-[140px] text-right z-eyebrow text-muted-foreground">
                    Updated
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableStateRow
                    colSpan={6}
                    state="loading"
                    message="Loading recipes…"
                  />
                ) : isError ? (
                  <TableStateRow
                    colSpan={6}
                    state="error"
                    message="Couldn't load recipes — the server didn't answer in time."
                    onRetry={() => refetch()}
                  />
                ) : rows.length === 0 ? (
                  <TableStateRow
                    colSpan={6}
                    state="empty"
                    message={
                      hasActiveFilters
                        ? "No recipes match your filters."
                        : "No recipes yet. Use Create recipe to build the first one."
                    }
                  />
                ) : (
                  rows.map((recipe) => {
                    const openRecipe = () =>
                      navigate(
                        `/settings/nutrition/add-recipe?id=${recipe.id}`,
                        { state: { recipeListSearch: location.search } }
                      );
                    return (
                      <TableRow
                        key={recipe.id}
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={openRecipe}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openRecipe();
                          }
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{recipe.name}</span>
                            {recipe.status === "draft" ? (
                              <span className="z-eyebrow text-warning">
                                Draft
                              </span>
                            ) : recipe.food_code ? (
                              <span className="z-eyebrow text-muted-foreground tabular-nums">
                                {recipe.food_code}
                                {recipe.food_version_number != null
                                  ? ` · v${recipe.food_version_number}`
                                  : ""}
                              </span>
                            ) : null}
                            {/* A published recipe can also have a staged new
                                version — surface it so it's not hidden. */}
                            {recipe.status === "submitted" &&
                              recipe.has_unpublished_changes && (
                                <DraftInProgressPill compact />
                              )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {recipe.category ? (
                            <Badge variant="neutral">{recipe.category}</Badge>
                          ) : (
                            <span className="z-caption text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {recipe.servings_per_recipe}
                        </TableCell>
                        {/* ?? not ||, so a genuine 0 kcal still reads "0"
                            rather than collapsing into the no-data dash. */}
                        <TableCell className="text-right">
                          {recipe.kcal_per_serving ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {recipe.ingredient_count}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatRelative(recipe.updated_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewRecipes;
